import { useState } from "react";
import { LinearProgress, Modal, ModalTitle, ModalContent, ModalActions, Button, Alert, IconButton, Table, TableRow, TableCell, TableBody, Grid, Typography, TextField } from "@deere/fuel-react"
import { useOktaAuth } from "@okta/okta-react";
import { validateSupplierNumbersByChunk } from "../../services/external.service";
import { addSupplierToCampaign, saveContactData, saveNonDeereContactData, savePartsForSurvey } from "../../services/campaign.service";
import { useUserInfo } from "../../providers/userProvider";
import { CRS_REQUEST_STATUS_OBJ, SUPPLIER_IS_INVALID, SUPPLIER_IS_VALID, LBL_DANGER, LBL_SUCCESS, SUPPLIER_ALREADY_EXISTS } from "../../utils/surveyLiterals";
import AutocompleteSupplierContacts from "./AutocompleteSupplierContacts";
import AutocompleteDeereContacts from "./AutocompleteDeereContacts";
import { ReactComponent as DeleteIcon } from "@deere/ux.brand-foundations/icons/delete.svg";
import ModalAddParts from "./ModalAddParts";
import { useTranslation } from "react-i18next";

const ModalAddSupplier = ({ showModal, setShowModal, labelBtnPrimary, labelBtnSecondary, titleModal, reload, campaignId, campaignResponses, isNonDeere, proceedToAddParts }) => {
  const [suppNo, setSuppNo] = useState("");
  const [suppName, setSuppName] = useState("");
  const [formattedSupp, setFormattedSupp] = useState("");
  const [alert, setAlert] = useState({ show: false, variant: "", title: "", bgColor: "" });
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);
  const [isFieldEmpty, setIsFieldEmpty] = useState(true);
  const [supplierContacts, setSupplierContacts] = useState([]);
  const [deereContacts, setDeereContacts] = useState([]);
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [parts, setParts] = useState([]);
  const { authState } = useOktaAuth();
  const userInfo = useUserInfo();
  const { t } = useTranslation();

  const suppChangeHandler = (e) => {
    if(e.target.name === "suppNo"){
      let suppFormat = e.target.value.trim().padStart(10, "0");
      setSuppNo(e.target.value);
      setFormattedSupp(suppFormat);
    }else{
      setSuppName(e.target.value);
    }
    if (!isNonDeere) {
      setValid(false);
      e.target.value === "" ? setIsFieldEmpty(true) : setIsFieldEmpty(false);
      setAlert((prevState) => {
        return { ...prevState, show: false };
      });
    } else {
      if (e.target.name === "suppNo") {
        e.target.value === "" || suppName === "" ? setIsFieldEmpty(true) : setIsFieldEmpty(false);
      } else {
        e.target.value === "" || suppNo === "" ? setIsFieldEmpty(true) : setIsFieldEmpty(false);
      }
    }
  };

  const validateSupplier = () => {
    setLoading(true);
    if (isNonDeere) {
      let alreadyExists = campaignResponses.find((resp) => resp.supplierName === `${formattedSupp} - ${suppName}`);
      if (alreadyExists) {
        setAlert((prevState) => {
          return { ...prevState, show: true, variant: LBL_DANGER, title: SUPPLIER_ALREADY_EXISTS, bgColor: "#f6dbde" };
        });
        setLoading(false);
      } else {
        setAlert((prevState) => {
          return { ...prevState, show: true, variant: LBL_SUCCESS, title: SUPPLIER_IS_VALID, bgColor: "#d6e5d3" };
        });
        setValid(true);
        setLoading(false);
      }
    } else {
      let alreadyExists = campaignResponses.find((resp) => resp.supplierNumber === formattedSupp);
      if (alreadyExists) {
        setAlert((prevState) => {
          return { ...prevState, show: true, variant: LBL_DANGER, title: SUPPLIER_ALREADY_EXISTS, bgColor: "#f6dbde" };
        });
        setLoading(false);
      } else {
        validateSupplierNumbersByChunk(authState.accessToken.accessToken, [formattedSupp])
          .then((sup) => {
            if (sup.length === 0) {
              setAlert((prevState) => {
                return { ...prevState, show: true, variant: LBL_SUCCESS, title: SUPPLIER_IS_VALID, bgColor: "#d6e5d3" };
              });
              setValid(true);
            } else {
              setAlert((prevState) => {
                return { ...prevState, show: true, variant: LBL_DANGER, title: SUPPLIER_IS_INVALID, bgColor: "#f6dbde" };
              });
            }
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
          });
      }
    }
  };

  const saveSupplier = () => {
    setLoading(true);
    let reqObj = {
      campaignId: campaignId,
      supplierNumber: formattedSupp,
      active: true,
      updatedBy: userInfo.userID,
      status: CRS_REQUEST_STATUS_OBJ.EMAIL_PENDING,
    };
    addSupplierToCampaign(reqObj, authState.accessToken.accessToken)
      .then((res) => {
        if (res && res.statusCode === 200) {
          if (supplierContacts.length > 0 || deereContacts.length > 0) {
            saveContacts(res);
          } else if (proceedToAddParts) {
            saveParts(res.assignmentId);
          } else {
            setShowModal(false);
            resetAllStates();
            setLoading(false);
            reload();
          }
        } else if (res && res.statusCode === 400) {
          setAlert((prevState) => {
            return { ...prevState, show: true, variant: LBL_DANGER, title: res.message, bgColor: "#f6dbde" };
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setAlert((prevState) => {
          return { ...prevState, show: true, variant: LBL_DANGER, title: err.message, bgColor: "#f6dbde" };
        });
        setLoading(false);
      });
  };

  const saveContacts = (res) => {
    if (res && res.assignmentId) {
      let arrToSave = [];
      if (isNonDeere) {
        const objToSave = {
          assignmentId: res.assignmentId,
          createdBy: userInfo.userID,
          updatedBy: userInfo.userID,
        };
        supplierContacts.forEach((item) => arrToSave.push({ ...objToSave, contactEmail: item.value, isCompanyContact: false }));
        deereContacts.forEach((item) => arrToSave.push({ ...objToSave, contactEmail: item.value, isCompanyContact: true }));
        saveNonDeereContactData(arrToSave, authState.accessToken.accessToken)
          .then((res) => {
            if (proceedToAddParts) {
              saveParts(objToSave.assignmentId);
            } else {
              setShowModal(false);
              resetAllStates();
              setLoading(false);
              reload();
            }
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
          });
      } else {
        const objToSave = {
          campaignAssignmentId: res.assignmentId,
          createdBy: userInfo.userID,
          updatedBy: userInfo.userID,
        };
        supplierContacts.forEach((item) => arrToSave.push({ ...objToSave, userId: item.value, deereContact: false }));
        deereContacts.forEach((item) => arrToSave.push({ ...objToSave, userId: item.value, deereContact: true }));
        saveContactData(arrToSave, authState.accessToken.accessToken)
          .then((res) => {
            if (proceedToAddParts) {
              saveParts(objToSave.campaignAssignmentId);
            } else {
              setShowModal(false);
              resetAllStates();
              setLoading(false);
              reload();
            }
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
          });
      }
    }
  };

  const saveParts = (assignmentId) => {
    const reqObj = {
      assignmentId: assignmentId,
      supplierNumber: formattedSupp,
      supplierName: suppName,
      isNonDeere: isNonDeere,
      userId: userInfo.userID,
      parts: parts.map((part) => ({ partNumber: part.partNumber, partDescription: part.partDescription, hsCode: part.hsCode })),
    };
    savePartsForSurvey(authState.accessToken.accessToken, reqObj)
      .then((res) => {
        setShowAddPartModal(false);
        closeModal();
        setLoading(false);
        reload();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const deleteSupplierContacts = (value) => {
    setSupplierContacts((prevState) => {
      let newData = prevState.filter((item) => item.value !== value);
      return [...newData];
    });
  };

  const deleteDeereContacts = (value) => {
    setDeereContacts((prevState) => {
      let newData = prevState.filter((item) => item.value !== value);
      return [...newData];
    });
  };

  const resetAllStates = () => {
    setSuppNo("");
    setSuppName("");
    setFormattedSupp("");
    setAlert({ show: false, variant: "", title: "", bgColor: "" });
    setValid(false);
    setSupplierContacts([]);
    setDeereContacts([]);
    setParts([]);
  };

  const closeModal = () => {
    resetAllStates();
    setShowModal(false);
  };

  return (
    <>
      <Modal open={showModal} onClose={closeModal} fullWidth>
        <ModalTitle>{titleModal}</ModalTitle>
        <ModalContent style={{ padding: "20px" }} dividers>
          {alert.show && (
            <Alert variant={alert.variant} aria-atomic="true" style={{ background: alert.bgColor }}>
              <Typography variant="h5">{alert.title}</Typography>
            </Alert>
          )}
          <TextField placeholder="Enter Supplier Number" value={suppNo} onChange={suppChangeHandler} disabled={loading} name="suppNo" type="number" />
          {isNonDeere && <TextField style={{ marginTop: "10px" }} placeholder="Enter Supplier Name" value={suppName} onChange={suppChangeHandler} disabled={loading} name="suppName" />}
          <Grid container justifyContent="flex-end" mt={2}>
            <Button variant="secondary" onClick={validateSupplier} disabled={loading || isFieldEmpty}>
              Validate Supplier Number
            </Button>
            <Grid item xs={12}>
              {loading && <LinearProgress size="sm" />}
            </Grid>
          </Grid>
          <hr />
          <Grid container>
            <Typography variant="p" size="xl">
              Supplier Contacts
            </Typography>
            <Table size="small">
              <TableBody>
                {supplierContacts.map((data) => {
                  return (
                    <TableRow key={data.value}>
                      <TableCell>{data.label}</TableCell>
                      <TableCell>
                        <IconButton ariaLabel="Delete" onClick={() => deleteSupplierContacts(data.value)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <AutocompleteSupplierContacts supplierNumber={formattedSupp} supplierContacts={supplierContacts} setSupplierContacts={setSupplierContacts} valid={valid} isNonDeere={isNonDeere} campaignId={campaignId} />
          </Grid>
          <hr />
          <Grid container>
            <Typography variant="p" size="xl">
              {isNonDeere ? "Company Contact" : "Deere Contacts"}
            </Typography>
            <Table size="small">
              <TableBody>
                {deereContacts.map((data) => {
                  return (
                    <TableRow key={data.value}>
                      <TableCell>{data.label}</TableCell>
                      <TableCell>
                        <IconButton ariaLabel="Delete" onClick={() => deleteDeereContacts(data.value)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <AutocompleteDeereContacts deereContacts={deereContacts} setDeereContacts={setDeereContacts} valid={valid} isNonDeere={isNonDeere} />
          </Grid>
        </ModalContent>
        <ModalActions>
          <Button variant="secondary" onClick={closeModal} disabled={loading}>
            {labelBtnSecondary}
          </Button>
          <Button type="submit" variant="primary" onClick={proceedToAddParts ? () => setShowAddPartModal(true) : saveSupplier} disabled={loading || !valid}>
            {labelBtnPrimary}
          </Button>
        </ModalActions>
      </Modal>
      <ModalAddParts
        showModal={showAddPartModal}
        setShowModal={setShowAddPartModal}
        labelBtnSecondary={t("btn_cancel")}
        labelBtnPrimary={t("btn_save_survey")}
        titleModal={t("lbl_add_parts")}
        saveSupplier={saveSupplier}
        parts={parts}
        setParts={setParts}
      />
    </>
  );
};

export default ModalAddSupplier;
